import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { DcrAttachment } from '../Attachments/attachment.types';
import { AttachmentDropzone } from '../Attachments/AttachmentDropzone/AttachmentDropzone';
import { AttachmentGrid } from '../Attachments/AttachmentGrid/AttachmentGrid';
import type { AddFilesResult } from '../Attachments/useAttachmentUpload';

import { ComplianceConfirmation } from './ComplianceConfirmation/ComplianceConfirmation';
import { ReviewNotes } from './ReviewNotes/ReviewNotes';
import { reviewStepActionsStyles as sx } from './ReviewStepActions.styles';

type Props = {
	onBack: () => void;
	onClose: () => void;
	attachments: DcrAttachment[];
	addFiles: (files: File[]) => AddFilesResult;
	removeAttachment: (id: string) => void;
	isSubmitting: boolean;
	submitError: boolean;
};

export const ReviewStepActions = ({
	onBack,
	onClose,
	attachments,
	addFiles,
	removeAttachment,
	isSubmitting,
	submitError
}: Props) => {
	const { t } = useTranslation();
	const { control } = useFormContext();

	const isThirdPartyDataConfirmed = useWatch({
		control,
		name: 'isThirdPartyDataConfirmed'
	});

	const isUploading = attachments.some(att => att.status === 'uploading');

	return (
		<Box sx={sx.root}>
			<AttachmentDropzone attachments={attachments} addFiles={addFiles} />
			<AttachmentGrid
				attachments={attachments}
				removeAttachment={removeAttachment}
				size='large'
			/>

			<ReviewNotes control={control} expandOnFocus />

			<Divider sx={sx.divider} />

			<ComplianceConfirmation control={control} />

			<Divider sx={sx.divider} />

			{submitError && (
				<Alert sx={sx.error} severity='error'>
					{t('An error occurred. Please try again.')}
				</Alert>
			)}

			<Box sx={sx.actions}>
				<Box sx={sx.backButtonContainer}>
					<Button
						variant='text'
						onClick={onBack}
						disabled={isSubmitting}
						startIcon={<NavigateBeforeIcon fontSize='small' />}
					>
						{t('Back')}
					</Button>
				</Box>

				<Button variant='text' onClick={onClose} disabled={isSubmitting}>
					{t('Cancel')}
				</Button>

				<Button
					type='submit'
					variant='contained'
					loading={isSubmitting}
					disabled={!isThirdPartyDataConfirmed || isUploading || isSubmitting}
					sx={sx.submitBtn}
				>
					{t('Submit')}
				</Button>
			</Box>
		</Box>
	);
};
